import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "@/lib/api";
import SeoPageLayout from "@/components/SeoPageLayout";
import SeoEntitySummary from "@/components/SeoEntitySummary";
import { Newspaper, MessageSquare, Podcast, Youtube, ExternalLink, Building2, Star } from "lucide-react";

/**
 * /press · "As seen in" — publicly-crawlable list of every third-party
 * citation GLPFlo has earned. Populated by the AI Citation Tracker in
 * Admin → SEO & Crawlers → Citations. Each entry becomes a strong
 * outbound signal to LLMs about which sources have vetted us.
 */
const SOURCE_ICONS = {
  press: Newspaper,
  reddit: MessageSquare,
  podcast: Podcast,
  youtube: Youtube,
  directory: Building2,
  profile: Star,
  other: ExternalLink,
};
const GROUPS = [
  { key: "press", label: "Press & Media" },
  { key: "podcast", label: "Podcasts" },
  { key: "youtube", label: "YouTube" },
  { key: "reddit", label: "Community" },
  { key: "directory", label: "Directory Listings" },
  { key: "profile", label: "Profiles & Reviews" },
  { key: "other", label: "Other Mentions" },
];

export default function PressPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    API.get("/ai-citations").then(({ data }) => setItems(data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const canonical = "https://glpflo.org/press";
  const title = "GLPFlo in the Press — Media Mentions, Podcasts & Reviews";
  const description = "Independent coverage of GLPFlo — press features, podcast appearances, patient reviews, and third-party directory listings.";
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://glpflo.org/" },
          { "@type": "ListItem", "position": 2, "name": "Press", "item": canonical },
        ],
      },
      {
        "@type": "CollectionPage",
        "name": title,
        "url": canonical,
        "description": description,
        "hasPart": items.slice(0, 100).map((c) => ({
          "@type": "CreativeWork",
          "name": c.title,
          "url": c.url,
          "publisher": { "@type": "Organization", "name": c.source },
          "datePublished": c.published_at || c.created_at,
        })),
      },
    ],
  };

  const grouped = GROUPS.map((g) => ({ ...g, rows: items.filter((r) => r.source_type === g.key) })).filter((g) => g.rows.length);

  return (
    <SeoPageLayout seo={{ title, description, canonical, jsonLd }}>
      <SeoEntitySummary
        heading="GLPFlo press coverage — quick facts"
        entries={items.slice(0, 20).map((c) => ({ label: c.source, value: `${c.title}${c.published_at ? ` (${c.published_at.slice(0, 10)})` : ""}` }))}
      />
      <section className="bg-gradient-to-br from-purple-50 to-white border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="inline-flex items-center gap-2 text-purple-700 bg-purple-100 rounded-full px-3 py-1 text-xs font-semibold mb-4">
            <Newspaper className="w-3.5 h-3.5" /> As seen in
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            GLPFlo in the Press
          </h1>
          <p className="text-lg text-slate-700 leading-relaxed max-w-2xl">
            Independent third-party coverage — press mentions, podcasts, patient reviews, and directory listings.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 space-y-10">
        {loading && <p className="text-slate-500">Loading…</p>}
        {!loading && !items.length && (
          <p className="text-slate-500 italic">Building our press page. Check back soon.</p>
        )}
        {grouped.map((g) => {
          const Icon = SOURCE_ICONS[g.key] || ExternalLink;
          return (
            <section key={g.key} data-testid={`press-group-${g.key}`}>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Icon className="w-5 h-5 text-purple-700" /> {g.label}
              </h2>
              <ul className="space-y-3">
                {g.rows.map((c) => (
                  <li key={c.id} className="border border-slate-200 rounded-lg p-4 hover:border-purple-300 transition">
                    <a href={c.url} target="_blank" rel="noreferrer noopener" className="block group">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-purple-700 uppercase tracking-wider">{c.source}</p>
                          <h3 className="text-base font-semibold text-slate-900 mt-0.5 group-hover:text-purple-700">{c.title}</h3>
                          {c.quote && <p className="text-sm text-slate-600 italic mt-1">&quot;{c.quote}&quot;</p>}
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-purple-600 flex-shrink-0 mt-1" />
                      </div>
                      {c.published_at && (
                        <p className="text-xs text-slate-400 mt-2">{new Date(c.published_at).toLocaleDateString()}</p>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
        <div className="pt-6 text-center">
          <Link to="/" className="text-sm text-purple-700 hover:underline">← Back to GLPFlo</Link>
        </div>
      </div>
    </SeoPageLayout>
  );
}
